import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ChatColors } from '../../../configs/chat-colors';
import { Box, Flex } from '@updater/ui-uds';
import ReactMarkdown from 'react-markdown';
import { File } from '@phosphor-icons/react';

// Types
import { Message } from '../../types';

// Components
import { MessageBubble } from '../message-bubble/MessageBubble';

// Types
export interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  animateLastAssistant?: boolean;
  onTypewriterDone?: () => void;
  endRef?: React.RefObject<HTMLDivElement | null>;
}

// Constants
const TYPEWRITER_SPEED = 25;
const TYPEWRITER_BLINK_ID = 'typewriter-blink';

// Styled Components
const MessagesContainer = styled(Box)`
  flex: 1;
  overflow: visible;
  padding: ${ChatColors.messagelist.PADDING};
  display: flex;
  flex-direction: column;
  gap: ${ChatColors.messagelist.GAP};
  background-color: ${ChatColors.messagelist.CONTAINER_BG};

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${ChatColors.messagelist.SCROLLBAR_TRACK};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${ChatColors.messagelist.SCROLLBAR_THUMB};
    border-radius: 3px;
    &:hover {
      background: ${ChatColors.messagelist.SCROLLBAR_THUMB_HOVER};
    }
  }
`;

const MarkdownContainer = styled.div<{ isUser: boolean }>`
  color: inherit;
  font-size: 1rem;
  line-height: 1.5;
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.5rem 0;
    font-weight: 600;
    color: inherit;
  }
  h1 {
    font-size: 1.25rem;
  }
  h2 {
    font-size: 1.125rem;
  }
  h3 {
    font-size: 1rem;
  }
  h4,
  h5,
  h6 {
    font-size: 0.875rem;
  }
  p {
    margin: 0.5rem 0;
    color: inherit;
  }
  ul,
  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    color: inherit;
  }
  li {
    margin: 0.25rem 0;
    color: inherit;
  }
  strong,
  b {
    font-weight: 600;
    color: inherit;
  }
  em,
  i {
    font-style: italic;
    color: inherit;
  }
  code {
    background-color: ${ChatColors.messagelist.MARKDOWN_CODE_BG};
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.8em;
    color: inherit;
  }
  pre {
    background-color: ${ChatColors.messagelist.MARKDOWN_PRE_BG};
    padding: 0.75rem;
    border-radius: 0.5rem;
    overflow-x: auto;
    margin: 0.5rem 0;
    color: inherit;
  }
  pre code {
    background: none;
    padding: 0;
    font-size: 0.8em;
  }
  blockquote {
    border-left: 3px solid ${ChatColors.messagelist.BLOCKQUOTE_BORDER};
    padding-left: 1rem;
    margin: 0.5rem 0;
    color: ${ChatColors.messagelist.BLOCKQUOTE_TEXT};
    font-style: italic;
  }
  a {
    color: ${ChatColors.messagelist.LINK};
    text-decoration: underline;
  }
  a:hover {
    color: ${ChatColors.messagelist.LINK};
  }
  hr {
    border: none;
    border-top: 1px solid ${ChatColors.messagelist.HR};
    margin: 1rem 0;
  }
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 0.5rem 0;
  }
  th,
  td {
    border: 1px solid ${ChatColors.messagelist.TABLE_BORDER};
    padding: 0.5rem;
    text-align: left;
    color: inherit;
  }
  th {
    background-color: ${ChatColors.messagelist.TABLE_HEADER_BG};
    font-weight: 600;
  }
`;

const AttachmentsRow = styled(Flex)`
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const AttachmentChip = styled(Flex)`
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.6rem;
  border-radius: 9999px;
  background-color: ${ChatColors.filechip.BG};
  border: ${ChatColors.filechip.BORDER};
  color: ${ChatColors.filechip.TEXT};
  font-size: 0.875rem;
  line-height: 1;
  margin-bottom: 0.8rem;
`;

const ChipText = styled.span`
  font-size: 0.875rem;
  line-height: 1;
  color: ${ChatColors.filechip.TEXT};
`;

const useBlinkingCursorSetup = () => {
  useEffect(() => {
    if (
      typeof document !== 'undefined' &&
      !document.getElementById(TYPEWRITER_BLINK_ID)
    ) {
      const style = document.createElement('style');
      style.id = TYPEWRITER_BLINK_ID;
      style.innerHTML = `
        @keyframes blink { 
          0%, 50% { opacity: 1; } 
          51%, 100% { opacity: 0; } 
        }
        @keyframes fadeInText {
          0% { opacity: 0.3; }
          100% { opacity: 1; }
        }
        @keyframes cursorBlink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.3; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
};

// Utility Functions
const findLastAssistantMessageIndex = (messages: Message[]): number => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (!messages[i].isUser) return i;
  }
  return -1;
};

// Component Helpers
const AttachmentsList: React.FC<{
  attachments: Message['attachments'];
  messageId: string;
}> = ({ attachments, messageId }) => {
  if (!attachments || attachments.length === 0) return null;

  return (
    <AttachmentsRow>
      {attachments.map((attachment, idx) => (
        <AttachmentChip key={`${messageId}-att-${idx}`}>
          <File size={14} color={ChatColors.filechip.TEXT} />
          {attachment.url ? (
            <a href={attachment.url} target="_blank" rel="noreferrer">
              <ChipText>{attachment.name}</ChipText>
            </a>
          ) : (
            <ChipText>{attachment.name}</ChipText>
          )}
        </AttachmentChip>
      ))}
    </AttachmentsRow>
  );
};

// Typewriter hook (restored)
const useTypewriter = (
  text: string,
  speed: number = TYPEWRITER_SPEED,
  key?: string,
  onDone?: () => void
) => {
  const [displayed, setDisplayed] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [showCursor, setShowCursor] = useState(false);

  // Helper function to find what text should be displayed (hides incomplete markdown)
  const getDisplayableText = (
    fullText: string,
    currentIndex: number
  ): string => {
    let text = fullText.slice(0, currentIndex);
    let lastOpenBold = -1;
    let pos = 0;
    while (pos < text.length) {
      const boldStart = text.indexOf('**', pos);
      if (boldStart === -1) break;
      const boldEnd = text.indexOf('**', boldStart + 2);
      if (boldEnd === -1) {
        lastOpenBold = boldStart;
        break;
      } else {
        pos = boldEnd + 2;
      }
    }
    if (lastOpenBold !== -1) {
      const beforeBold = text.slice(0, lastOpenBold);
      const incompleteBoldPart = text.slice(lastOpenBold);
      let replacement = '';
      if (
        incompleteBoldPart.startsWith('**') ||
        incompleteBoldPart.startsWith('*')
      ) {
        replacement = '_';
      }
      text = beforeBold + replacement;
    }
    return text;
  };

  // Helper to check if current char is a boundary (sentence/bullet)
  const isPauseBoundary = (prev: string, nextChar: string) => {
    // Sentence end: . ! ?
    if (['.', '!', '?'].includes(prev.slice(-1)) && nextChar !== ' ') {
      return true;
    }
    // Newline (for bullets/lists)
    if (nextChar === '\n') return true;
    // Markdown bullet/numbered list
    if (/^[-*] $/.test(prev.slice(-2)) || /^\d+\. $/.test(prev)) return true;
    return false;
  };

  useEffect(() => {
    setDisplayed('');
    setIsTyping(true);
    setShowCursor(false);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      const nextChar = text[i] || '';
      const currentText = getDisplayableText(text, i);
      setDisplayed(currentText);
      // Show cursor if at boundary and not at end
      if (
        i < text.length &&
        (isPauseBoundary(currentText, nextChar) || nextChar === '\n')
      ) {
        setShowCursor(true);
      } else {
        setShowCursor(false);
      }
      if (i >= text.length) {
        clearInterval(interval);
        setIsTyping(false);
        setShowCursor(false);
        if (onDone) onDone();
      }
    }, speed);
    return () => {
      clearInterval(interval);
    };
    // Only restart if text or key changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, speed, key]);

  return { displayed, isTyping, showCursor };
};

// Dedicated typewriter message component for animation
const TypewriterMessage: React.FC<{
  message: Message;
  speed?: number;
  onDone?: () => void;
}> = ({ message, speed = TYPEWRITER_SPEED, onDone }) => {
  const { displayed, isTyping, showCursor } = useTypewriter(
    message.text,
    speed,
    message.id,
    onDone
  );
  return (
    <div
      style={{
        minHeight: '1.5rem',
        transition: 'opacity 0.2s ease',
        opacity: isTyping ? 0.9 : 1,
      }}
    >
      {displayed ? (
        <ReactMarkdown>
          {showCursor ? displayed + ' _' : displayed}
        </ReactMarkdown>
      ) : (
        <div style={{ opacity: 0.3 }}>Preparing message...</div>
      )}
      <AttachmentsList
        attachments={message.attachments}
        messageId={message.id}
      />
    </div>
  );
};

const MessageContent: React.FC<{
  message: Message;
}> = ({ message }) => (
  <>
    <ReactMarkdown>{message.text}</ReactMarkdown>
    <AttachmentsList attachments={message.attachments} messageId={message.id} />
  </>
);

// Main Component
const MessageListComponent: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  animateLastAssistant = false,
  onTypewriterDone,
  endRef,
}) => {
  // Setup hooks
  useBlinkingCursorSetup();

  // State management
  const [lastAnimatedId, setLastAnimatedId] = useState<string | null>(null);

  // Smooth loading transition state
  const [isLoadingVisible, setIsLoadingVisible] = useState(isLoading);
  const [loadingOpacity, setLoadingOpacity] = useState(isLoading ? 1 : 0);

  // Typewriter delay state to allow loading fade-out
  const [typewriterCanStart, setTypewriterCanStart] = useState(!isLoading);

  // Handle loading transition effect
  useEffect(() => {
    if (isLoading && !isLoadingVisible) {
      setIsLoadingVisible(true);
      setLoadingOpacity(1);
      setTypewriterCanStart(false); // Block typewriter while loading
    } else if (!isLoading && isLoadingVisible) {
      setLoadingOpacity(0);
      const timer = setTimeout(() => {
        setIsLoadingVisible(false);
      }, 300); // Match fade duration

      // Allow typewriter to start after a brief delay for smooth transition
      const typewriterTimer = setTimeout(() => {
        setTypewriterCanStart(true);
      }, 200); // Start typewriter 200ms after loading ends (during fade-out)

      return () => {
        clearTimeout(timer);
        clearTimeout(typewriterTimer);
      };
    } else if (!isLoading && !isLoadingVisible) {
      setTypewriterCanStart(true);
    }
    return undefined;
  }, [isLoading, isLoadingVisible]);

  // Calculate typewriter animation state
  const lastAssistantIdx = findLastAssistantMessageIndex(messages);
  const lastAssistantId =
    lastAssistantIdx !== -1 ? messages[lastAssistantIdx].id : '';

  const shouldAnimateTypewriter =
    animateLastAssistant &&
    !isLoading &&
    typewriterCanStart &&
    lastAssistantIdx === messages.length - 1 &&
    !messages[lastAssistantIdx]?.isUser &&
    messages[lastAssistantIdx]?.id !== lastAnimatedId;

  // Handle animation completion
  useEffect(() => {
    // Reset if a new message arrives
    if (lastAssistantId !== lastAnimatedId && !isLoading) {
      setLastAnimatedId(null);
    }
  }, [lastAssistantId, lastAnimatedId, isLoading]);

  // Render function for individual messages
  const renderMessage = (message: Message, index: number) => {
    const isLastAssistantMessage =
      !message.isUser && index === lastAssistantIdx && !isLoading;

    // Only show as animated if typewriter is actually ready and should animate
    const isAnimatedMessage = isLastAssistantMessage && shouldAnimateTypewriter;

    // For the last assistant message that should animate but typewriter isn't ready yet,
    // don't render anything to prevent flash of full text
    const shouldHideMessage =
      isLastAssistantMessage &&
      animateLastAssistant &&
      !shouldAnimateTypewriter &&
      !typewriterCanStart;

    if (shouldHideMessage) {
      return null;
    }

    return (
      <MessageBubble key={message.id} isUser={message.isUser}>
        <MarkdownContainer isUser={message.isUser}>
          {isAnimatedMessage ? (
            <TypewriterMessage
              message={message}
              speed={TYPEWRITER_SPEED}
              onDone={() => {
                setLastAnimatedId(message.id);
                onTypewriterDone?.();
              }}
            />
          ) : (
            <MessageContent message={message} />
          )}
        </MarkdownContainer>
      </MessageBubble>
    );
  };

  // Render loading message with smooth fade transition
  const AnimatedLoadingMessage: React.FC<{
    visible: boolean;
    opacity: number;
    messages: Message[];
  }> = ({ visible, opacity, messages }) => {
    const [dotCount, setDotCount] = React.useState(1);
    React.useEffect(() => {
      if (!visible) return;
      const interval = setInterval(() => {
        setDotCount((prev) => (prev === 3 ? 1 : prev + 1));
      }, 300);
      return () => clearInterval(interval);
    }, [visible]);
    // Check if any pending assistant message has attachments and no text
    const lastMsg = messages[messages.length - 1];
    const hasPendingAttachment =
      lastMsg &&
      lastMsg.isUser &&
      lastMsg.attachments &&
      lastMsg.attachments.length > 0;
    const animatedMessage = hasPendingAttachment
      ? `Assistant is scanning document${'.'.repeat(dotCount)}`
      : `Assistant is thinking${'.'.repeat(dotCount)}`;
    if (!visible) return null;
    return (
      <MessageBubble isUser={false}>
        <MarkdownContainer isUser={false}>
          <div
            style={{
              minHeight: '24px', // Stable minimum height
              lineHeight: '1.5',
              fontSize: '1rem',
              opacity,
              transition: 'opacity 0.3s ease-out',
            }}
          >
            <ReactMarkdown>{animatedMessage}</ReactMarkdown>
          </div>
        </MarkdownContainer>
      </MessageBubble>
    );
  };

  return (
    <MessagesContainer role="log" aria-live="polite" aria-label="Chat messages">
      {messages.map(renderMessage)}
      <AnimatedLoadingMessage
        visible={!!isLoadingVisible}
        opacity={typeof loadingOpacity === 'number' ? loadingOpacity : 1}
        messages={messages}
      />
      <div ref={endRef} style={{ height: '0.25rem', minHeight: '0.25rem' }} />
    </MessagesContainer>
  );
};

export const MessageList = React.memo(
  MessageListComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.messages === nextProps.messages &&
      prevProps.animateLastAssistant === nextProps.animateLastAssistant
    );
  }
);

export default MessageList;
